// src/api/utils/clientErrorHandler.ts

/**
 * Client-side error handling utilities
 *
 * This module provides user-friendly error messages for client-side errors
 * that occur during API calls. It follows the same architectural pattern as
 * the backend error_utils.py for consistency.
 *
 * Key principles:
 * 1. Categorize errors by TYPE, not by message content
 * 2. Centralize error messages in a mapping object
 * 3. Separate "what happened" from "what to do"
 * 4. Let backend handle business logic errors (validation, permissions, etc.)
 *
 * Architecture:
 * Error → Type Detection (instanceof) → Message Lookup → User Message
 *
 * What we handle:
 * - Network errors (TypeError from fetch)
 * - Request cancellation (AbortError)
 * - Invalid JSON responses (SyntaxError)
 *
 * What backend handles (passed through unchanged):
 * - File access errors ("file locked in another app")
 * - Validation errors ("required field missing")
 * - Permission errors
 * - All business logic errors
 */

/**
 * Client-side error categories
 *
 * These categories represent errors that occur on the client-side BEFORE
 * or DURING communication with the server. Business logic errors (file access,
 * validation, permissions) are handled by the backend and passed through unchanged.
 *
 * Based on Fetch API best practices:
 * - fetch() only throws TypeError for network/CORS/invalid URL
 * - AbortController throws DOMException with name 'AbortError'
 * - response.json() can throw SyntaxError for invalid JSON
 */
import { SUPPORT_EMAIL } from "../../config/constants";

enum ClientErrorCategory {
  /** Network error - cannot reach server (TypeError from fetch) */
  NETWORK_ERROR = "network_error",
  /** User cancelled the request (DOMException with name 'AbortError') */
  REQUEST_CANCELLED = "request_cancelled",
  /** Invalid JSON response from server (SyntaxError from response.json()) */
  INVALID_JSON = "invalid_json",
  /** Unknown client-side error */
  UNKNOWN = "unknown",
}

/**
 * Structure for error message definitions
 * Matches backend's error message format
 */
interface ErrorMessageDefinition {
  /** Short title for the error category */
  title: string;
  /** What happened (user-friendly explanation) */
  message: string;
  /** What the user should do about it */
  suggestion: string;
  /** Standardized error code for tracking */
  errorCode: string;
}

/**
 * Information about a client-side error with user-friendly messaging
 */
export interface ClientErrorInfo {
  /**
   * User-friendly error message with actionable steps
   * Example: "Unable to process your files. If any files are currently open in other applications, please close them and try again."
   */
  message: string;

  /**
   * Error code that indicates the type of client error
   * Examples: "CONNECTION_FAILED", "FILE_ACCESS_ERROR", "CONNECTION_INTERRUPTED"
   */
  errorCode: string;

  /**
   * Whether the error is likely recoverable by user action
   */
  isRecoverable: boolean;
}

/**
 * Context in which the error occurred
 * This helps provide more relevant error messages
 */
export type ErrorContext = "file_upload" | "streaming" | "general";

/**
 * Centralized error message mapping
 * Similar to backend's LLM_ERROR_MESSAGES
 *
 * Each error category has:
 * - title: Short error type name
 * - message: What happened (user-friendly)
 * - suggestion: What the user should do
 * - errorCode: Standardized code for tracking
 *
 * NOTE: We intentionally keep this minimal. Business logic errors (file access,
 * validation, permissions, etc.) are handled by the backend and their error
 * messages are passed through unchanged.
 */
const CLIENT_ERROR_MESSAGES: Record<ClientErrorCategory, ErrorMessageDefinition> = {
  [ClientErrorCategory.NETWORK_ERROR]: {
    title: "Connection Lost",
    message: "The connection was interrupted before we could finish your request.",
    suggestion: `Please check your internet connection and try again. If the issue persists, contact ${SUPPORT_EMAIL}`,
    errorCode: "CONNECTION_FAILED",
  },
  [ClientErrorCategory.REQUEST_CANCELLED]: {
    title: "Request Cancelled",
    message: "Request was cancelled.",
    suggestion: "", // Silent - user intentionally cancelled the request
    errorCode: "REQUEST_CANCELLED",
  },
  [ClientErrorCategory.INVALID_JSON]: {
    title: "Invalid Response",
    message: "We couldn't complete your request due to a temporary issue.",
    suggestion: `Please try again in a couple of mins & if the issue persists, contact ${SUPPORT_EMAIL}`,
    errorCode: "INVALID_RESPONSE",
  },
  [ClientErrorCategory.UNKNOWN]: {
    title: "Unexpected Error",
    message: "Something unexpected happened.",
    suggestion: `Please try again in a couple of mins & if the issue persists, contact ${SUPPORT_EMAIL}`,
    errorCode: "UNKNOWN_ERROR",
  },
};

/**
 * Detect the error category based on error type and properties
 *
 * This function uses proper error type checking instead of string matching,
 * following Fetch API best practices:
 *
 * - TypeError: Network errors, CORS, invalid URLs (from fetch())
 * - DOMException with name 'AbortError': User cancelled request
 * - SyntaxError: Invalid JSON from response.json()
 *
 * We do NOT use string matching (msg.includes) because:
 * - Error messages vary across browsers (Chrome vs Firefox)
 * - Error messages can change between versions
 * - Business logic errors should come from backend, not be detected here
 *
 * @param error The error to categorize
 * @returns The detected error category
 */
function detectErrorCategory(error: unknown): ClientErrorCategory {
  // 1. Check for AbortError (user cancelled request)
  // This is a DOMException with name 'AbortError'
  if (error instanceof DOMException && error.name === "AbortError") {
    return ClientErrorCategory.REQUEST_CANCELLED;
  }

  // 2. Check for network errors (cannot reach server)
  // fetch() throws TypeError for: network failures, CORS, invalid URLs
  if (error instanceof TypeError) {
    return ClientErrorCategory.NETWORK_ERROR;
  }

  // 3. Check for JSON parsing errors (invalid response)
  // response.json() throws SyntaxError when JSON is malformed
  if (error instanceof SyntaxError) {
    return ClientErrorCategory.INVALID_JSON;
  }

  // 4. Also check error.name for SyntaxError (some environments)
  if (error instanceof Error && error.name === "SyntaxError") {
    return ClientErrorCategory.INVALID_JSON;
  }

  // 5. Fallback for unknown client-side errors
  // This should rarely happen - most errors will be caught above
  return ClientErrorCategory.UNKNOWN;
}

/**
 * Create user-friendly error information for client-side errors
 *
 * This function follows a simplified pattern:
 * 1. Detect error category (using type checking, not string matching)
 * 2. Look up message from centralized mapping
 * 3. Combine message + suggestion for final output
 *
 * Note: The 'context' parameter is kept for backwards compatibility but is
 * no longer used. Business logic errors (file access, validation) are handled
 * by the backend and their messages are passed through unchanged.
 *
 * @param error The error that occurred (can be any type)
 * @param context The context in which the error occurred (deprecated, kept for compatibility)
 * @returns ClientErrorInfo with user-friendly message and error code
 *
 * @example
 * // For a network error:
 * const errorInfo = createClientErrorInfo(error);
 * // Returns: { message: "We couldn't reach our service. Please try again...", errorCode: "CONNECTION_FAILED", isRecoverable: true }
 */
export function createClientErrorInfo(
  error: unknown,
  _context: ErrorContext = "general"
): ClientErrorInfo {
  // Step 1: Detect error category based on error type
  const category = detectErrorCategory(error);

  // Step 2: Look up error message definition
  const errorDef = CLIENT_ERROR_MESSAGES[category];

  // Step 3: Combine message and suggestion
  const fullMessage = errorDef.suggestion
    ? `${errorDef.message} ${errorDef.suggestion}`
    : errorDef.message;

  // Step 4: Determine if error is recoverable
  // Only REQUEST_CANCELLED and UNKNOWN are not recoverable
  const isRecoverable =
    category !== ClientErrorCategory.REQUEST_CANCELLED && category !== ClientErrorCategory.UNKNOWN;

  return {
    message: fullMessage,
    errorCode: errorDef.errorCode,
    isRecoverable,
  };
}

/**
 * Quick helper to get just the error message
 * Useful when you only need the message string
 *
 * @param error The error that occurred
 * @param context The context in which the error occurred
 * @returns User-friendly error message
 */
export function getClientErrorMessage(error: unknown, context: ErrorContext = "general"): string {
  return createClientErrorInfo(error, context).message;
}

/**
 * Quick helper to get just the error code
 * Useful for logging or tracking specific error types
 *
 * @param error The error that occurred
 * @param context The context in which the error occurred
 * @returns Error code string
 */
export function getClientErrorCode(error: unknown, context: ErrorContext = "general"): string {
  return createClientErrorInfo(error, context).errorCode;
}
